import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  Boxes,
  ExternalLink,
  ListPlus,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { UomSelect } from '@/components/inventory/UomSelect';
import { WorkItemPicker } from '@/components/work/WorkItemPicker';
import { QuietEmpty } from '../shared';
import { useBomComponents, type BomComponent } from '@/features/workMode/hooks';
import {
  useDeleteStationPart,
  useSaveStationPart,
  useStationParts,
} from '@/features/workMode/stationSetup';
import { describeWorkError } from '@/features/workMode/api';
import type { WorkLookups } from '@/features/workMode/useLookups';
import type { FloorWorkstation, WorkstationPart } from '@/features/workMode/types';

interface StationPartsTabProps {
  station: FloorWorkstation;
  lookups: WorkLookups;
  canManage: boolean;
}

function qtyText(p: { quantity: number | null; uom: string | null }): string | null {
  if (p.quantity === null) return null;
  const n = String(p.quantity);
  return p.uom ? `${n} ${p.uom}` : n;
}

/**
 * The components and consumables that have to be stocked at the bench:
 * what, how many to keep on hand, and which bin they live in. Parts can be
 * catalog items (picked, or pulled straight from the default item's BOM)
 * or free text for the tape and zip ties nobody tracks.
 */
export function StationPartsTab({ station, lookups, canManage }: StationPartsTabProps) {
  const { data: parts = [], isLoading } = useStationParts(station.id);
  const save = useSaveStationPart();
  const remove = useDeleteStationPart();
  const [editing, setEditing] = useState<WorkstationPart | 'new' | null>(null);
  const [fromBom, setFromBom] = useState(false);

  const linkedItemIds = useMemo(
    () => new Set(parts.map((p) => p.item_id).filter((id): id is string => !!id)),
    [parts],
  );
  const nextPosition = ((parts.at(-1)?.position ?? 0) as number) + 10;

  const move = (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= parts.length) return;
    const order = [...parts];
    [order[idx], order[j]] = [order[j], order[idx]];
    void Promise.all(
      order.map((p, i) => {
        const position = (i + 1) * 10;
        if (p.position === position) return Promise.resolve();
        return save.mutateAsync({
          id: p.id,
          stationId: station.id,
          itemId: p.item_id,
          name: p.name,
          quantity: p.quantity,
          uom: p.uom,
          binLocation: p.bin_location,
          notes: p.notes,
          position,
        });
      }),
    ).catch((e) => toast.error(describeWorkError(e)));
  };

  const del = (p: WorkstationPart) => {
    if (!window.confirm(`Remove "${p.name}" from this station?`)) return;
    remove.mutate(
      { id: p.id, stationId: station.id },
      {
        onSuccess: () => toast.success('Part removed.'),
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {parts.length === 0
            ? 'What has to be in the trays before a shift starts.'
            : `${parts.length} part${parts.length === 1 ? '' : 's'} stocked at this bench.`}
        </p>
        {canManage && (
          <div className="flex items-center gap-2">
            {station.default_item_id && (
              <Button size="sm" variant="outline" onClick={() => setFromBom(true)}>
                <ListPlus className="h-4 w-4" />
                From BOM
              </Button>
            )}
            <Button size="sm" onClick={() => setEditing('new')}>
              <Plus className="h-4 w-4" />
              Add part
            </Button>
          </div>
        )}
      </div>

      {isLoading ? null : parts.length === 0 ? (
        <QuietEmpty>
          <span className="flex flex-col items-center gap-2">
            <Boxes className="h-6 w-6" />
            No parts listed yet.
            {canManage && station.default_item_id && (
              <span className="text-xs">
                Start from the BOM of {lookups.itemName(station.default_item_id)}.
              </span>
            )}
          </span>
        </QuietEmpty>
      ) : (
        <ol className="divide-y divide-border rounded-2xl border border-border bg-card">
          {parts.map((p, i) => {
            const sku = p.item_id ? lookups.itemSku(p.item_id) : null;
            const qty = qtyText(p);
            return (
              <li key={p.id} className="flex items-start gap-3 px-4 py-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-secondary text-muted-foreground">
                  <Boxes className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium text-foreground">{p.name}</p>
                    {p.item_id ? (
                      <Link
                        to={`/catalog/items/${p.item_id}`}
                        className="inline-flex items-center gap-1 text-xs text-info hover:underline"
                      >
                        {sku ?? 'Catalog item'}
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                    ) : (
                      <Badge variant="muted">Consumable</Badge>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {[
                      qty ? `Keep ${qty} on hand` : null,
                      p.bin_location ? `Bin ${p.bin_location}` : null,
                      p.notes,
                    ]
                      .filter(Boolean)
                      .join(' · ') || 'No quantity or bin set'}
                  </p>
                </div>
                {canManage && (
                  <div className="flex shrink-0 items-center gap-0.5">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => move(i, -1)}
                      disabled={i === 0 || save.isPending}
                      aria-label="Move up"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => move(i, 1)}
                      disabled={i === parts.length - 1 || save.isPending}
                      aria-label="Move down"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditing(p)}
                      aria-label={`Edit ${p.name}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-fresh-salmon-dark"
                      onClick={() => del(p)}
                      disabled={remove.isPending}
                      aria-label={`Remove ${p.name}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {editing !== null && (
        <PartDialog
          key={editing === 'new' ? 'new' : editing.id}
          station={station}
          lookups={lookups}
          part={editing === 'new' ? null : editing}
          nextPosition={nextPosition}
          onClose={() => setEditing(null)}
        />
      )}
      {fromBom && station.default_item_id && (
        <FromBomDialog
          station={station}
          itemId={station.default_item_id}
          itemName={lookups.itemName(station.default_item_id) ?? 'the default item'}
          alreadyLinked={linkedItemIds}
          nextPosition={nextPosition}
          onClose={() => setFromBom(false)}
        />
      )}
    </div>
  );
}

/** Mounted only while open (keyed by part id) so a background refetch never wipes a half-filled form. */
function PartDialog({
  station,
  lookups,
  part,
  nextPosition,
  onClose,
}: {
  station: FloorWorkstation;
  lookups: WorkLookups;
  part: WorkstationPart | null;
  nextPosition: number;
  onClose: () => void;
}) {
  const save = useSaveStationPart();
  const [itemId, setItemId] = useState<string | null>(part?.item_id ?? null);
  const [name, setName] = useState(part?.name ?? '');
  const [quantity, setQuantity] = useState(part?.quantity != null ? String(part.quantity) : '');
  const [uom, setUom] = useState(part?.uom ?? '');
  const [bin, setBin] = useState(part?.bin_location ?? '');
  const [notes, setNotes] = useState(part?.notes ?? '');
  const [picking, setPicking] = useState(false);

  const pickItem = (id: string | null) => {
    setItemId(id);
    if (id) {
      const n = lookups.itemName(id);
      if (n && (!name.trim() || name === lookups.itemName(itemId))) setName(n);
    }
  };

  const submit = () => {
    if (!name.trim()) {
      toast.error('Give the part a name.');
      return;
    }
    const qty = quantity.trim() === '' ? null : Number(quantity);
    if (qty !== null && (!Number.isFinite(qty) || qty < 0)) {
      toast.error('Quantity has to be a number, zero or more.');
      return;
    }
    save.mutate(
      {
        id: part?.id,
        stationId: station.id,
        itemId,
        name,
        quantity: qty,
        uom,
        binLocation: bin,
        notes,
        ...(part ? {} : { position: nextPosition }),
      },
      {
        onSuccess: () => {
          toast.success(part ? 'Part updated.' : 'Part added.');
          onClose();
        },
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{part ? 'Edit part' : 'Add a part'}</DialogTitle>
          <DialogDescription>Stocked at {station.name}.</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="space-y-1.5">
            <Label>Catalog item (optional)</Label>
            <div className="flex items-center gap-2">
              <div className="flex min-h-10 flex-1 items-center rounded-lg border border-border bg-background px-3 text-sm">
                {itemId ? (
                  <span className="truncate">
                    {lookups.itemName(itemId)}{' '}
                    <span className="text-muted-foreground">{lookups.itemSku(itemId)}</span>
                  </span>
                ) : (
                  <span className="text-muted-foreground">
                    Not a tracked SKU (tape, wipes, ties…)
                  </span>
                )}
              </div>
              <Button type="button" variant="outline" onClick={() => setPicking(true)}>
                Choose
              </Button>
              {itemId && (
                <Button type="button" variant="ghost" onClick={() => setItemId(null)}>
                  Clear
                </Button>
              )}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="part-name">Name</Label>
            <Input
              id="part-name"
              value={name}
              maxLength={160}
              onChange={(e) => setName(e.target.value)}
              placeholder="M4 × 12 button-head screws"
              autoFocus={!itemId}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="part-qty">Keep on hand</Label>
              <Input
                id="part-qty"
                inputMode="decimal"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="200"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="part-uom">Unit</Label>
              <UomSelect id="part-uom" value={uom} onChange={setUom} emptyLabel="No unit" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="part-bin">Bin at the bench</Label>
              <Input
                id="part-bin"
                value={bin}
                onChange={(e) => setBin(e.target.value)}
                placeholder="Tray 3 / blue"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="part-notes">Notes</Label>
            <Textarea
              id="part-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Restock from warehouse bin C-12. Check for burrs."
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending || !name.trim()}>
              {save.isPending ? 'Saving…' : part ? 'Save' : 'Add part'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      <WorkItemPicker
        open={picking}
        onOpenChange={setPicking}
        stationId={station.id}
        currentItemId={itemId}
        onPick={pickItem}
        title="Which part is stocked here?"
      />
    </Dialog>
  );
}

/** Tick the BOM lines of the default item to add them as parts in one go. */
function FromBomDialog({
  station,
  itemId,
  itemName,
  alreadyLinked,
  nextPosition,
  onClose,
}: {
  station: FloorWorkstation;
  itemId: string;
  itemName: string;
  alreadyLinked: Set<string>;
  nextPosition: number;
  onClose: () => void;
}) {
  const { data: components = [], isLoading } = useBomComponents(itemId);
  const save = useSaveStationPart();
  const candidates = useMemo(
    () => components.filter((c) => !alreadyLinked.has(c.item_id)),
    [components, alreadyLinked],
  );
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [busy, setBusy] = useState(false);

  // Default everything on once the BOM arrives.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || isLoading || candidates.length === 0) return;
    seeded.current = true;
    setPicked(new Set(candidates.map((c) => c.item_id)));
  }, [isLoading, candidates]);

  const toggle = (id: string) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  };

  const submit = async () => {
    const rows: BomComponent[] = candidates.filter((c) => picked.has(c.item_id));
    if (rows.length === 0) return;
    setBusy(true);
    let position = nextPosition;
    let failed = 0;
    for (const c of rows) {
      try {
        await save.mutateAsync({
          stationId: station.id,
          itemId: c.item_id,
          name: c.name,
          quantity: null,
          uom: c.uom,
          position,
        });
        position += 10;
      } catch {
        failed += 1;
      }
    }
    setBusy(false);
    if (failed) toast.error(`${failed} part${failed === 1 ? '' : 's'} could not be added.`);
    else toast.success(`${rows.length} part${rows.length === 1 ? '' : 's'} added from the BOM.`);
    onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Parts from the BOM</DialogTitle>
          <DialogDescription>
            Components of {itemName}. Set the on-hand quantity and bin afterwards.
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Reading the BOM…</p>
        ) : components.length === 0 ? (
          <QuietEmpty>{itemName} has no active BOM.</QuietEmpty>
        ) : candidates.length === 0 ? (
          <QuietEmpty>Every BOM component is already listed here.</QuietEmpty>
        ) : (
          <ul className="max-h-[50vh] divide-y divide-border overflow-y-auto rounded-xl border border-border">
            {candidates.map((c) => (
              <li key={c.item_id}>
                <label className="flex cursor-pointer items-center gap-3 px-3 py-2.5 hover:bg-secondary/60">
                  <Checkbox
                    checked={picked.has(c.item_id)}
                    onCheckedChange={() => toggle(c.item_id)}
                    ariaLabel={c.name}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground">
                      {c.name}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {c.sku} · {c.quantity} {c.uom} per unit
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={busy || picked.size === 0}>
            {busy ? 'Adding…' : `Add ${picked.size || ''} part${picked.size === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
